import { BACKEND_KEYS } from '../../common/consts/app-keys.const';
import {
  HttpFactoryService,
  EnhancedWithAuthHttpService
} from '../../common/services';
import { Ikey, IProductLicense, IOfflineActivationResult } from '../types.type';

class ManagementService {
  httpService: EnhancedWithAuthHttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createAuthHttpService();
  }

  async getLicenseKeys() {
    const response: Ikey[] | void = await this.httpService.get(
      BACKEND_KEYS.GET_LICENSE_KEYS
    );
    return response;
  }

  async getUserPortalUrl() {
    const portalUrl: string | void = await this.httpService.get(
      BACKEND_KEYS.GET_USER_PORTAL_URL
    );

    if (!portalUrl) throw new Error('Error getting portal');

    return portalUrl;
  }

  async releaseAllActivations(springId: string) {
    await this.httpService.post(BACKEND_KEYS.RELEASE_ACTIVATIONS, { springId });
  }

  async getSubscriptionUpdateUrl(subscriptionId: string) {
    const url: string | void = await this.httpService.get(
      `${BACKEND_KEYS.GET_SUBSCRIPTION_UPDATE_PORTAL_URL}?subscriptionId=${subscriptionId}`
    );

    if (!url) throw new Error('Error getting subscription update portal');

    return url;
  }

  getFile(os: string, product?: string): string {
    const baseUrl =
      process.env.NODE_ENV === 'development'
        ? `${process.env.REACT_APP_BACKEND_URL_DEV}/api/v1`
        : `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
    const productParam = product ? `&product=${encodeURIComponent(product)}` : '';
    return `${baseUrl}/${BACKEND_KEYS.PLUGIN}?os=${os}${productParam}`;
  }

  // Admin-granted product entitlements (product slugs) for the logged-in account.
  async getMyAccess(): Promise<string[]> {
    const response: { products: string[] } | void = await this.httpService.get(
      BACKEND_KEYS.MY_ACCESS
    );
    return response ? response.products : [];
  }

  // Squarebit Eye EULA clickwrap state for the signed-in account — whether the
  // CURRENT EULA version has been accepted (the download endpoint requires it).
  async getEyeEulaStatus(): Promise<{ accepted: boolean }> {
    const response: { accepted: boolean } | void = await this.httpService.get(
      BACKEND_KEYS.EYE_EULA
    );
    return response ?? { accepted: false };
  }

  /** Claim the free Eye tier. Idempotent: clicking twice is a no-op. */
  async claimFreeEye(): Promise<{ ok: boolean; licensed: boolean }> {
    const result = await this.httpService.post(BACKEND_KEYS.EYE_FREE_CLAIM, {});
    return (result ?? { ok: false, licensed: false }) as {
      ok: boolean;
      licensed: boolean;
    };
  }

  // Records the explicit "I agree to the Squarebit Eye EULA" acceptance for a
  // granted account (clickwrap; unlocks the package download server-side).
  async acceptEyeEula(eulaVersion: string) {
    return this.httpService.post(BACKEND_KEYS.EYE_EULA_ACCEPT, {
      eula: eulaVersion
    });
  }

  // Licences from Squarebit's own licence server (separate from LicenseSpring
  // keys, which getLicenseKeys returns).
  //
  // ANY failure reads as "no licences". While LICENSING_ENABLED is off the
  // endpoint 404s by design, and that must render as an absent section — not as
  // an error toast on My Products for every customer who has never heard of the
  // Eye.
  async getMyLicenses(): Promise<IProductLicense[]> {
    try {
      const response: IProductLicense[] | void = await this.httpService.get(
        BACKEND_KEYS.MY_LICENSES
      );
      return Array.isArray(response) ? response : [];
    } catch {
      return [];
    }
  }

  // Offline (air-gapped) activation: the user carries a request blob here from
  // a machine with no outbound network, and carries the signed .lic back.
  // Errors are rethrown rather than swallowed — unlike getMyLicenses, this is
  // an explicit user action and its failure reason is the whole message.
  async activateOffline(request: string): Promise<IOfflineActivationResult> {
    const response: IOfflineActivationResult | void = await this.httpService.post(
      BACKEND_KEYS.OFFLINE_ACTIVATE,
      { request }
    );
    if (!response) throw new Error('Offline activation failed');
    return response;
  }

  // Free the seat a machine holds. Ownership is enforced server-side.
  async releaseActivation(activationId: number) {
    return this.httpService.post(
      `${BACKEND_KEYS.RELEASE_ACTIVATION}/${activationId}/release`,
      {}
    );
  }

  // Grant-gated full SquarebitEye package. Fetched with credentials so a 403
  // (grant revoked) can be surfaced instead of silently failing the download.
  getEyePackageUrl(variant?: string, version?: string): string {
    const baseUrl =
      process.env.NODE_ENV === 'development'
        ? `${process.env.REACT_APP_BACKEND_URL_DEV}/api/v1`
        : `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
    const params = new URLSearchParams();
    if (variant) params.set('variant', variant);
    if (version) params.set('version', version);
    const query = params.toString();
    return `${baseUrl}/${BACKEND_KEYS.EYE_PACKAGE}${query ? `?${query}` : ''}`;
  }

  /**
   * Published Eye versions, newest first.
   *
   * Same shape and same failure handling as getFrameworkVersions: an empty
   * list used to mean both "no older builds" and "could not ask", so an
   * outage rendered as nothing at all. `githubReachable` separates them. A
   * failure of our OWN api is different again - we have not observed anything
   * about GitHub, so it reports reachable and shows no warning rather than
   * blaming an outage we cannot see.
   */
  async getEyeVersions(): Promise<{
    versions: { version: string; platforms: string[] }[];
    githubReachable: boolean;
    currentVersion: string | null;
  }> {
    try {
      const response:
        | {
            versions: { version: string; platforms: string[] }[];
            githubReachable: boolean;
            currentVersion: string | null;
          }
        | void = await this.httpService.get(BACKEND_KEYS.EYE_VERSIONS);
      if (response && Array.isArray(response.versions)) {
        return {
          versions: response.versions,
          githubReachable: response.githubReachable !== false,
          currentVersion: response.currentVersion ?? null
        };
      }
      return { versions: [], githubReachable: true, currentVersion: null };
    } catch {
      return { versions: [], githubReachable: true, currentVersion: null };
    }
  }

  // Squarebit Framework EULA clickwrap state for the signed-in account -
  // whether the CURRENT version has been accepted (the download requires it).
  async getFrameworkEulaStatus(): Promise<{ accepted: boolean }> {
    const response: { accepted: boolean } | void = await this.httpService.get(
      BACKEND_KEYS.SBFRAMEWORK_EULA
    );
    return response ?? { accepted: false };
  }

  // Records the explicit "I agree to the Squarebit Framework EULA" acceptance
  // for a granted account (clickwrap; unlocks the download server-side).
  async acceptFrameworkEula(eulaVersion: string) {
    return this.httpService.post(BACKEND_KEYS.SBFRAMEWORK_EULA_ACCEPT, {
      eula: eulaVersion
    });
  }

  // Grant-gated Squarebit Framework module zip, per platform. Fetched with
  // credentials so a 403 (grant revoked, or the EULA version bumped since this
  // page loaded) can be surfaced instead of silently failing the download.
  // Versions this account may download, newest first, plus whether the release
  // list could be reached at all.
  //
  // An empty list used to mean both "no older builds" and "could not ask", so
  // an outage rendered as nothing at all. Only the latest build is mirrored
  // onto the server, so when GitHub is unreachable the older ones really cannot
  // be served and the page says so instead of hiding them.
  //
  // A failure of our own API is different again: we cannot tell what GitHub is
  // doing, so it reports reachable and shows no warning rather than blaming an
  // outage we have not observed.
  async getFrameworkVersions(): Promise<{
    versions: { version: string; platforms: string[] }[];
    githubReachable: boolean;
    localLatest: boolean;
  }> {
    try {
      const response:
        | {
            versions: { version: string; platforms: string[] }[];
            githubReachable: boolean;
            localLatest: boolean;
          }
        | void = await this.httpService.get(BACKEND_KEYS.SBFRAMEWORK_VERSIONS);
      if (response && Array.isArray(response.versions)) {
        return {
          versions: response.versions,
          githubReachable: response.githubReachable !== false,
          localLatest: response.localLatest === true
        };
      }
      return { versions: [], githubReachable: true, localLatest: false };
    } catch {
      return { versions: [], githubReachable: true, localLatest: false };
    }
  }

  /**
   * URL for one file in the bundled documentation.
   *
   * A plain link rather than a fetch: the reference is a generated static site
   * whose pages link to each other and to their own stylesheets, so it has to
   * be navigated to, not pulled into the app. The server applies the same
   * grant check to every file under it.
   */
  getFrameworkDocUrl(docPath: string): string {
    const baseUrl =
      process.env.NODE_ENV === 'development'
        ? `${process.env.REACT_APP_BACKEND_URL_DEV}/api/v1`
        : `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
    return `${baseUrl}/user/sbframework-docs/${docPath}`;
  }

  /**
   * A JSON file from the same gated docs tree.
   *
   * Deliberately a bare `fetch` rather than the shared HttpService: that one
   * raises a site-wide error event on any failure, and a 404 here is an
   * ordinary answer, not a fault. It means the build being mirrored predates
   * the API reference, which the page says in a sentence rather than in a
   * banner across the site.
   *
   * No custom headers, so the request stays a simple one and does not spend a
   * preflight to say nothing. The session cookie carries the grant.
   */
  async getFrameworkDocJson<T>(docPath: string): Promise<T | null> {
    const response = await fetch(this.getFrameworkDocUrl(docPath), {
      credentials: 'include'
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Could not load ${docPath} (${response.status})`);
    }
    return (await response.json()) as T;
  }

  getFrameworkPackageUrl(os: string, version?: string): string {
    const baseUrl =
      process.env.NODE_ENV === 'development'
        ? `${process.env.REACT_APP_BACKEND_URL_DEV}/api/v1`
        : `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
    const versionParam = version ? `&version=${encodeURIComponent(version)}` : '';
    return `${baseUrl}/${BACKEND_KEYS.SBFRAMEWORK_PACKAGE}?os=${encodeURIComponent(os)}${versionParam}`;
  }
}

const managementService = new ManagementService(new HttpFactoryService());

export { managementService };
